import { useTranslation } from "react-i18next";
import { Users, Briefcase, ShieldCheck, RefreshCw, BadgeCheck, CalendarClock } from "lucide-react";
import { AED } from "../lib/api";

export const VehicleCard = ({ vehicle, mode, onSelect, onDetails, badge }) => {
  const { t } = useTranslation();
  const p = vehicle.pricing;
  const isMonthly = mode === "monthly";
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden card-lift fade-up flex flex-col ${badge ? "border-[#009900]" : "border-[#E2E8F0]"}`} data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="relative h-44 overflow-hidden bg-[#f4f7f4]">
        <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-[#144733] text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded" data-testid="vehicle-category-tag">
          {t("Group")} {vehicle.group} · {vehicle.group_label}
        </span>
        {badge && !isMonthly && (
          <span className={`absolute top-3 right-3 text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded ${badge === "Best value" ? "bg-[#009900] text-white" : "bg-[#FFDC00] text-[#0F1E17]"}`} data-testid="value-badge">
            {t(badge)}
          </span>
        )}
        {isMonthly && p.term_save_pct > 0 && (
          <span className="absolute top-3 right-3 bg-[#FFDC00] text-[#0F1E17] text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded" data-testid="term-save-badge">
            {t("Save {{pct}}%", { pct: p.term_save_pct })}
          </span>
        )}
        {isMonthly && p.term_save_pct === 0 && vehicle.model_guaranteed && (
          <span className="absolute top-3 right-3 bg-[#FFDC00] text-[#0F1E17] text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded flex items-center gap-1">
            <BadgeCheck className="w-3 h-3" /> {t("Model Guaranteed")}
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-baseline gap-2">
          <h3 className="text-lg font-bold text-[#0F1E17]">{vehicle.name}</h3>
          <span className="text-xs text-[#57685e]">{t("or similar")}</span>
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-[#57685e]">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{vehicle.seats}</span>
          <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{vehicle.luggage_large}L + {vehicle.luggage_small}S</span>
        </div>
        {isMonthly && (
          <div className="flex items-center gap-3 mt-2 text-[11px] text-[#57685e]">
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#009900]" />{(p.mileage_package || 3000).toLocaleString()} {t("km/mo")}</span>
            <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3 text-[#009900]" />MY {p.model_year}</span>
            {vehicle.swap_eligible && (
              <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3 text-[#009900]" />{t("Swap eligible")}</span>
            )}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-end justify-between border-t border-dashed border-[#E2E8F0]">
          <div className="pl-2 border-l-[3px] border-[#FFDC00]">
            {isMonthly ? (
              <>
                <div className="text-2xl font-bold text-[#0F1E17]">{AED(p.monthly_with_vat)}<span className="text-sm font-normal text-[#57685e]">{t("/mo")}</span></div>
                <div className="text-xs text-[#57685e]">{t("{{amount}} for {{n}} months · incl. VAT", { amount: AED(p.total_with_vat), n: p.months })}</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-[#0F1E17]">{AED(p.effective_daily_with_vat)}<span className="text-sm font-normal text-[#57685e]">{t("/day")}</span></div>
                <div className="text-xs text-[#57685e]">
                  {t("{{amount}} for {{n}} days · incl. VAT", { amount: AED(p.total_with_vat), n: p.days })}
                </div>
              </>
            )}
            <div className="text-[10px] text-[#57685e] mt-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-[#009900] shrink-0" /> {t("Zero-deposit option · Min age {{age}}", { age: vehicle.min_age })}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {onDetails && (
              <button onClick={() => onDetails(vehicle)} className="text-[11px] text-[#009900] font-semibold underline underline-offset-2 hover:text-[#144733]" data-testid="vehicle-card-details-btn">
                {t("View details")}
              </button>
            )}
            <button onClick={() => onSelect(vehicle)} className="btn-cta rounded px-4 py-2.5 text-xs" data-testid="vehicle-card-select-btn">
              {t("Book now")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
